import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { Control, Controller, UseFormSetValue } from 'react-hook-form';
import { Check, Search } from 'lucide-react';
import Checkbox from '@components/atoms/ui-kit/checkbox';
import Loader from '@components/atoms/loaders/loader';
import { WizardFormData, CustomerType } from '../create-customer-wizard-types';

interface Plan {
  id?: number;
  sku: string;
  type: string;
  description?: string;
}

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<WizardFormData, any>;
  setValue: UseFormSetValue<WizardFormData>;
  watchedValues: WizardFormData;
  plans: Plan[];
  isLoadingPlans: boolean;
  planSearch: string;
  setPlanSearch: (v: string) => void;
  selectedCustomerType: CustomerType;
}

export function CustomerWizardStep2({
  control,
  setValue,
  watchedValues,
  plans,
  isLoadingPlans,
  planSearch,
  setPlanSearch,
  selectedCustomerType
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  return (
    <div className="space-y-4">
      <p className="text-sm" style={{ color: getColour('inactiveText') }}>
        {t('select-plan-description')}
      </p>

      <div className="relative">
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4"
          style={{ color: getColour('inactiveText') }}
        />
        <input
          type="text"
          placeholder={t('search-plans')}
          value={planSearch}
          onChange={e => setPlanSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 text-sm rounded-2xl border"
          style={{
            backgroundColor: getColour('appSoft'),
            borderColor: getColour('hairline'),
            color: getColour('normalText')
          }}
        />
      </div>

      {isLoadingPlans ? (
        <div className="flex justify-center py-8">
          <Loader height={8} width={2} />
        </div>
      ) : plans.length === 0 ? (
        <p
          className="text-sm p-4 rounded-2xl text-center"
          style={{
            backgroundColor: getColour('thirdLayer'),
            color: getColour('inactiveText')
          }}
        >
          {planSearch.trim() ? t('no-matching-plans') : t('no-plans-available')}
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-64 overflow-y-auto">
            {plans.map(plan => {
              const isSelected = watchedValues.plan_id === plan.id;
              return (
                <button
                  key={plan.id}
                  type="button"
                  onClick={() =>
                    setValue('plan_id', isSelected ? undefined : plan.id)
                  }
                  className="p-3 rounded-2xl border text-left transition-colors"
                  style={{
                    borderColor: isSelected
                      ? getColour('activeText')
                      : getColour('hairline'),
                    backgroundColor: isSelected
                      ? getColour('thirdLayer')
                      : getColour('appSoft')
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <span
                        className="text-sm font-medium"
                        style={{ color: getColour('normalText') }}
                      >
                        {plan.sku}
                      </span>
                      <span
                        className="text-xs ml-2 px-1.5 py-0.5 rounded-2xl"
                        style={{
                          backgroundColor: getColour('thirdLayer'),
                          color: getColour('inactiveText')
                        }}
                      >
                        {plan.type}
                      </span>
                    </div>
                    {isSelected && (
                      <Check
                        className="h-4 w-4"
                        style={{ color: getColour('activeText') }}
                      />
                    )}
                  </div>
                  <p
                    className="text-xs mt-1"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {plan.description || t('no-description')}
                  </p>
                </button>
              );
            })}
          </div>

          {watchedValues.plan_id && (
            <Controller
              name="auto_renew"
              control={control}
              render={({ field }) => (
                <Checkbox
                  label={t('auto-renew-subscription')}
                  checked={field.value}
                  onChange={e => field.onChange(e.target.checked)}
                />
              )}
            />
          )}
        </>
      )}

      {selectedCustomerType === 'reseller' && watchedValues.plan_id && (
        <p
          className="text-xs p-2 rounded-2xl"
          style={{
            backgroundColor: getColour('thirdLayer'),
            color: getColour('inactiveText')
          }}
        >
          {t('sub-orgs-inherit-plan')}
        </p>
      )}
    </div>
  );
}
